// The single ViewModel bridge for the stem feature: a composition root that assembles the
// construct / filtering / manual-selection / actions pieces plus the selection-mode controller.
// StemPanel, StemSelection and StemTransform bind to this; none touches the Model (plannerStore)
// directly. The selection pieces live under selection/; actions at the root.
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { usePlannerStore } from '@/views/3d-planner/store/plannerStore';
import { useStemConstructs } from './selection/stemConstructs';
import { useStemFiltering } from './selection/stemFiltering';
import { useManualSelection } from './selection/stemManualSelection';
import { useStemActions } from './stemActions';
import { useStemSelectionMode } from './selection/stemSelectionMode';
import type { SelectionMode, StemOverlayViewModel } from './stem.types';

export const useStemOverlayViewModel: () => StemOverlayViewModel = defineStore('stem-overlay', () => {
    const planner = usePlannerStore();
    // Shared by the manual-selection state machine (reads it) and the mode controller (sets it).
    const selectionMode = ref<SelectionMode>(null);

    const constructs = useStemConstructs(planner);
    const filtering = useStemFiltering(constructs);
    const manual = useManualSelection({ planner, constructs, filtering, selectionMode });
    const actions = useStemActions({ planner });
    const mode = useStemSelectionMode({ planner, constructs, filtering, manual, selectionMode });

    return { ...constructs, ...filtering, ...manual, ...actions, ...mode, selectionMode };
});
